import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import {
	ALL_DRAFT_INVOICES_QUERY_KEY,
	useAllDraftInvoices,
} from "~/modules/billing/api/draftInvoice/draftInvoiceQueries.ts";
import { ALL_PROCESSED_INVOICES_QUERY_KEY } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import DeleteNonProjectDraftInvoiceForm
	from "~/modules/billing/components/DraftInvoicesIndexView/components/DraftNonProjectInvoiceDetailsSidebar/DeleteNonProjectDraftInvoiceForm";
import UpdateOrFinishNonProjectInvoiceForm
	from "~/modules/billing/components/DraftInvoicesIndexView/components/DraftNonProjectInvoiceDetailsSidebar/UpdateOrFinishNonProjectInvoiceForm";
import { createUnprocessedInvoiceTableDataFromNonProjectInvoice } from "~/modules/billing/utils/billingUtils.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	invoiceId?: string;
	userCanManageInvoices: boolean;
};

const DraftNonProjectInvoiceDetailsSidebar = ({
	isOpen,
	onClose,
	invoiceId,
	userCanManageInvoices,
}: Props) => {
	const [showDeletionConfirmation, setShowDeletionConfirmation] = useState<boolean>(false);

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data, options } = useAggregatedDataV2({
		allDraftInvoices: useAllDraftInvoices(),
	});

	const {
		allDraftInvoices,
	} = data;

	const enrichedInvoiceData = useMemo(() => {
		if (allDraftInvoices) {
			const invoice = allDraftInvoices.otherInvoices.find((inv) => inv.id === invoiceId);
			if (invoice) {
				return createUnprocessedInvoiceTableDataFromNonProjectInvoice(invoice);
			}
		}
		return null;
	}, [allDraftInvoices, invoiceId]);

	const handleSuccess = useCallback(async (billingProcessWasFinished: boolean) => {
		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		if (billingProcessWasFinished) {
			queryClient.invalidateQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });
			navigate(generatePath(INVOICE_DETAILS_PATH, { invoiceId }));
		} else {
			await queryClient.invalidateQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });
		}
		onClose();
	}, [invoiceId, navigate, onClose, queryClient]);

	const handleClose = useCallback(() => {
		setShowDeletionConfirmation(false);
		onClose();
	}, [onClose]);

	const dataReady = !!enrichedInvoiceData && !options.isLoading;
	const sidebarReady = isOpen && dataReady;
	const showDeletionConfirmationIsVisible = sidebarReady && showDeletionConfirmation;
	const showDetails = sidebarReady && !showDeletionConfirmationIsVisible;

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={handleClose}>
			{options.isLoading && <SidebarBusyOverlay />}
			{showDetails && <UpdateOrFinishNonProjectInvoiceForm draftInvoiceData={enrichedInvoiceData}
																 onDelete={() => setShowDeletionConfirmation(true)}
																 onCancel={handleClose}
																 onSuccess={handleSuccess}
																 userCanManageInvoices={userCanManageInvoices}
			/>

			}
			{showDeletionConfirmationIsVisible &&
				<DeleteNonProjectDraftInvoiceForm onCancel={() => setShowDeletionConfirmation(false)}
												  onSuccess={() => handleSuccess(false)}
												  invoiceData={enrichedInvoiceData} />}
		</Sidebar>
	);
};

export default DraftNonProjectInvoiceDetailsSidebar;
